import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent } from '@mui/material';
import { RenderFields } from './RenderFields';
import EmployeeAccordion from './EmployeeAccordion';
import { findSectionById, formatDateForInput } from '../../utils/employeeModalUtils';
import useEmployeeForm from '../../hooks/useEmployeeForm';
import { FieldsList } from '../../config/employeeFieldsConfig';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const EmployeeModal = ({ open, handleClose, employees, currentIndex, refreshData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(currentIndex);

  const currentEmployee = employees[currentEmployeeIndex] || {};
  const {
    formData,
    isEditMode,
    validationErrors,
    handleInputChange,
    handleSubmit,
    toggleEditMode,
    setEditModeFalse,
    handleDelete,
  } = useEmployeeForm(currentEmployee.id, handleClose);

  const employeeNameTitle = formData?.firstName || 'Employee';

  const formattedFormData = {
    ...formData,
    dateOfBirth: formatDateForInput(formData.dateOfBirth),
    startDate: formatDateForInput(formData.startDate),
  };

  const saveChanges = async () => {
    const success = await handleSubmit();
    if (success) {
      refreshData();
      handleClose();
      toggleEditMode(false);
    }
  };

  const deleteEmployee = async () => {
    await handleDelete();
    refreshData();
    handleClose();
    setEditModeFalse();
  };

  const handleNext = () => {
    if (currentEmployeeIndex + 1 < employees.length) {
      setCurrentEmployeeIndex(currentEmployeeIndex + 1);
      setEditModeFalse();
    }
  };

  const handlePrev = () => {
    if (currentEmployeeIndex - 1 >= 0) {
      setCurrentEmployeeIndex(currentEmployeeIndex - 1);
      setEditModeFalse();
    }
  };

  const handleModalClose = () => {
    setEditModeFalse();
    handleClose();
  };

  useEffect(() => {
    setCurrentEmployeeIndex(currentIndex);
  }, [currentIndex]);

  return (
    <>
      {open && (
        <>
          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            disabled={currentEmployeeIndex === 0}
            style={{
              position: 'fixed',
              left: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${currentEmployeeIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            aria-label="Previous Employee"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={handleNext}
            disabled={currentEmployeeIndex === employees.length - 1}
            style={{
              position: 'fixed',
              right: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className={`p-2 rounded-full bg-white hover:bg-gray-200 transition ${currentEmployeeIndex === employees.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            aria-label="Next Employee"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}

      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
          },
        }}
        fullWidth={true}
        style={{ zIndex: 1400 }}
      >
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleModalClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditMode ? `Edit Employee - ${employeeNameTitle}` : `View Employee - ${employeeNameTitle}`}
              </h2>
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          <div className="flex space-x-4 p-4">
            {isEditMode ? (
              <>
                <button onClick={toggleEditMode} className="px-4 py-2 bg-gray-300 text-blue-500 font-bold rounded-md hover:bg-gray-400 transition">
                  View
                </button>
                <button onClick={deleteEmployee} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition">
                  Delete
                </button>
                <button onClick={saveChanges} className="px-6 py-2 bg-yellow-500 font-bold rounded-md hover:bg-yellow-600 transition">
                  Save
                </button>
              </>
            ) : (
              <button onClick={toggleEditMode} className="px-6 py-2 bg-yellow-500 font-bold rounded-md hover:bg-yellow-600 transition">
                Edit
              </button>
            )}
          </div>
        </div>

        <DialogContent dividers>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="space-y-4">
              {/* Personal Info Section */}
              <EmployeeAccordion label="Personal Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'personalInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'accountInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>

              {/* Login Info Section */}
              <EmployeeAccordion label="Login Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'usernameInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'passwordInfo')?.fields || []}
                      formData={formData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>

              {/* Address Info Section */}
              <EmployeeAccordion label="Address/Date Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'addressInfo')?.fields || []}
                      formData={formattedFormData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'datesInfo')?.fields || []}
                      formData={formattedFormData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>

              {/* Contact Info Section */}
              <EmployeeAccordion label="Contact/Title Info">
                <div className={`flex ${isExpanded ? 'flex-row' : ''} w-5/6 place-self-center gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'contactInfo')?.fields || []}
                      formData={formattedFormData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={findSectionById(FieldsList, 'jobInfo')?.fields || []}
                      formData={formattedFormData}
                      handleChange={handleInputChange}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </EmployeeAccordion>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

EmployeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  employees: PropTypes.array.isRequired,
  currentIndex: PropTypes.number.isRequired,
  refreshData: PropTypes.func.isRequired,
};

export default EmployeeModal;
